import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import AuthImg from "@/components/AuthImg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSubscription } from "@/lib/useSubscription";
import TrialWelcomeHero from "@/components/TrialWelcomeHero";
import HowItWorks from "@/components/HowItWorks";
import UpsellDialog from "@/components/UpsellDialog";
import EarningsBar from "@/components/EarningsBar";
import OnboardingCard from "@/components/OnboardingCard";
import TakeoverNoticeBanner from "@/components/TakeoverNoticeBanner";
import ChurnInsightsWidget from "@/components/admin/ChurnInsightsWidget";
import { cn } from "@/lib/utils";
import { CASE_STATUS_META } from "@/lib/caseStatus";
import {
  Plus, FileText, Search, Camera, Loader2, ArrowRight, Clock, TrendingUp,
  Download, ImageOff, Trash2, AlertTriangle, ChevronLeft, ChevronRight, RefreshCw, CarFront,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

// Re-use the shared status dictionary so new workflow statuses (ready_for_gutachter,
// released, sent_to_customer, revision_requested, …) get correct labels here too.
const STATUS_META = CASE_STATUS_META;

// Define the workflow order — used to derive the "current step" for each case.
const WORKFLOW = [
  { key: "customer", label: "Kunde" },
  { key: "insurance", label: "Versicherung" },
  { key: "vehicle", label: "Fahrzeug" },
  { key: "photos", label: "Lichtbilder" },
  { key: "predamages", label: "Vorschäden" },
  { key: "documents", label: "Dokumente" },
  { key: "signature", label: "Unterschrift" },
  { key: "review", label: "Übersicht" },
];

function isComplete(c, key) {
  switch (key) {
    case "customer":   return !!(c.customer?.name || c.customer?.firstname);
    case "insurance":  return !!(c.insurance?.insurer_name || c.insurance?.claim_number);
    case "vehicle":    return !!(c.vehicle?.vin || c.vehicle?.license_plate);
    case "photos":     return (c.photos?.length || 0) > 0;
    case "predamages": return Object.values(c.pre_damages || {}).some((p) => (p?.statuses?.length || 0) > 0 || p?.status);
    case "documents":  return (c.documents?.length || 0) > 0;
    case "signature":  return !!c.signature;
    case "review":     return !!(c.damage_description || c.ai_generated_report);
    default: return false;
  }
}

function nextOpenStep(c) {
  for (const s of WORKFLOW) {
    if (!isComplete(c, s.key)) return s.label;
  }
  return "Fertig";
}

function calcStatus(c) {
  // Manuell vom Admin gesetzter Wert hat Vorrang.
  const manual = c?.calculation_status;
  if (manual === "not_calculated")
    return { key: "not_calculated", tone: "zinc", label: "Nicht kalkuliert", testid: "calc-status-pending" };
  if (manual === "calculated")
    return { key: "calculated", tone: "emerald", label: "Kalkuliert", testid: "calc-status-done" };
  if (manual === "completed")
    return { key: "completed", tone: "violet", label: "Abgeschlossen", testid: "calc-status-completed" };
  if (manual === "sent")
    return { key: "sent", tone: "cyan", label: "Versendet", testid: "calc-status-sent" };
  // Fallback aus Kalkulations-Summe (rückwärtskompatibel zu Altdaten).
  const total = Number(c?.calculation?.summary?.total_gross || 0);
  if (total > 0) {
    return { key: "calculated", tone: "emerald", label: "Kalkuliert", testid: "calc-status-done" };
  }
  const items = Array.isArray(c?.calculation?.items) ? c.calculation.items.length : 0;
  const inProgressStatus = ["uploaded", "in_review", "in_progress"].includes(c?.status);
  if (items > 0 || inProgressStatus) {
    return { key: "in_progress", tone: "amber", label: "Wird kalkuliert", testid: "calc-status-progress" };
  }
  return { key: "not_calculated", tone: "zinc", label: "Nicht kalkuliert", testid: "calc-status-pending" };
}

const CALC_STATUS_OPTIONS = [
  { value: "not_calculated", label: "Nicht kalkuliert" },
  { value: "calculated", label: "Kalkuliert" },
  { value: "completed", label: "Abgeschlossen" },
  { value: "sent", label: "Versendet" },
];

const CALC_TONES = {
  emerald: "bg-[#12A9E8]/10 text-[#12A9E8] border-[#12A9E8]/30",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  zinc: "bg-zinc-50 text-zinc-500 border-zinc-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
};
const CALC_DOTS = {
  emerald: "bg-[#12A9E8]",
  amber: "bg-amber-500",
  zinc: "bg-zinc-300",
  violet: "bg-violet-500",
  cyan: "bg-cyan-500",
};

function formatEUR(value) {
  if (value === null || value === undefined || value === "") return "–";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
}

function caseValue(c) {
  // Prefer DAT market value, then retail value, then any explicit `value_eur` field.
  const dat = c.vehicle?.dat_data || {};
  return dat.market_value_eur || dat.retail_value_eur || c.value_eur || null;
}

function thumbnailPath(c) {
  // Choose first ordered photo; fall back to first document image.
  const photos = (c.photos || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  if (photos[0]?.storage_path) return photos[0].storage_path;
  const imgDoc = (c.documents || []).find(
    (d) => (d.original_filename || "").toLowerCase().match(/\.(jpe?g|png|webp)$/) || d.doc_type === "fahrzeugschein"
  );
  return imgDoc?.storage_path || null;
}

// Dropdown im Dashboard, mit dem Admin/Backoffice/Mitarbeiter den
// Kalkulations-Status einer Akte manuell umschalten können.
function CalcStatusPicker({ c, canEdit, onUpdated, compact = false }) {
  const cs = calcStatus(c);
  const [saving, setSaving] = React.useState(false);

  if (!canEdit) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 font-semibold rounded-none border",
          compact ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-1",
          CALC_TONES[cs.tone],
        )}
        data-testid={`${cs.testid}-${c.case_number}`}
      >
        <span className={cn(compact ? "h-1 w-1" : "h-1.5 w-1.5", "rounded-full", CALC_DOTS[cs.tone])} />
        {cs.label}
      </span>
    );
  }

  const handleChange = async (next) => {
    if (next === cs.key) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/cases/${c.id}`, { calculation_status: next });
      toast.success(`Status: ${CALC_STATUS_OPTIONS.find((o) => o.value === next)?.label}`);
      onUpdated?.(data.case);
    } catch (e) {
      toast.error(apiError(e, "Status konnte nicht geändert werden"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Select value={cs.key} onValueChange={handleChange} disabled={saving}>
      <SelectTrigger
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "h-7 gap-1.5 font-semibold rounded-none border w-auto min-w-[140px]",
          compact ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2",
          CALC_TONES[cs.tone],
        )}
        data-testid={`calc-status-select-${c.case_number}`}
      >
        <span className="flex items-center gap-1.5">
          <span className={cn("h-1.5 w-1.5 rounded-full", CALC_DOTS[cs.tone])} />
          <SelectValue placeholder="Status" />
        </span>
      </SelectTrigger>
      <SelectContent>
        {CALC_STATUS_OPTIONS.map((opt) => (
          <SelectItem
            key={opt.value}
            value={opt.value}
            data-testid={`calc-status-option-${opt.value}`}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function Dashboard() {
  const { user, accessToken } = useAuth();
  const { isTrial, isActive } = useSubscription();
  const { t } = useTranslation();
  // „Neue" Akten: beim Gutachter Gutachten „Zur Freigabe", beim Akten-Team
  // frisch hochgeladene Akten (noch nicht übernommen) — grün hervorgehoben.
  const isTeamRole = ["admin", "mitarbeiter", "backoffice"].includes(user?.role);
  const isNewCase = React.useCallback(
    (c) => (isTeamRole
      ? c?.status === "uploaded" && !c?.team_opened_at
      : c?.status === "ready_for_gutachter" && !c?.owner_reopened_at),
    [isTeamRole]
  );
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [upsellHeadline, setUpsellHeadline] = useState(null);
  const [upsellSub, setUpsellSub] = useState(null);
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  // Zusätzliche Listen-Filter (entsprechen den Tabellen-Spalten)
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [stepFilter, setStepFilter] = useState("all");
  const [calcFilter, setCalcFilter] = useState("all");
  const [rangeFilter, setRangeFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // case object pending deletion
  const [deletingId, setDeletingId] = useState(null);

  // Bulk-Auswahl & -Löschen (nur Admin)
  const isAdmin = user?.role === "admin";
  // Kalkulations-Status-Dropdown ist für Admin/Backoffice/Mitarbeiter aktiv,
  // Gutachter sehen den Wert weiterhin als Badge ohne Edit-Möglichkeit.
  const canEditCalcStatus = ["admin", "backoffice", "mitarbeiter"].includes(user?.role);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  // Reset Seite bei Filter-Wechsel
  useEffect(() => { setPage(0); }, [search, status, ownerFilter, stepFilter, calcFilter, rangeFilter]);

  // Bearbeiter-Liste aus den geladenen Akten ableiten (für den Filter)
  const owners = useMemo(() => {
    const map = new Map();
    for (const c of cases) {
      if (!c.owner_id || map.has(c.owner_id)) continue;
      map.set(c.owner_id, c.owner_name || (c.owner_id === user?.id ? user?.name : null) || "Unbekannt");
    }
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "de"));
  }, [cases, user]);

  // Client-seitige Filterung passend zu den Tabellen-Spalten
  const filteredCases = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    return cases.filter((c) => {
      if (ownerFilter !== "all" && c.owner_id !== ownerFilter) return false;
      if (stepFilter !== "all" && nextOpenStep(c) !== stepFilter) return false;
      if (calcFilter !== "all" && calcStatus(c).key !== calcFilter) return false;
      if (rangeFilter !== "all") {
        const created = c.created_at ? new Date(c.created_at).getTime() : 0;
        if (rangeFilter === "today") {
          if (!created || new Date(created).toDateString() !== new Date().toDateString()) return false;
        } else {
          const days = rangeFilter === "7d" ? 7 : 30;
          if (!created || now - created > days * 86400000) return false;
        }
      }
      if (q) {
        const hay = [
          c.customer?.firstname, c.customer?.name, c.title, c.case_number, c.case_reference,
          c.vehicle?.license_plate, c.vehicle?.make, c.vehicle?.model, c.owner_name,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    }).sort((a, b) =>
      // Neue Akten immer zuoberst: Gutachter „Zur Freigabe", Team „Hochgeladen"
      (isNewCase(b) ? 1 : 0) - (isNewCase(a) ? 1 : 0)
    );
  }, [cases, search, ownerFilter, stepFilter, calcFilter, rangeFilter, isNewCase]);

  const activeFilterCount =
    (search.trim() ? 1 : 0) + (status !== "all" ? 1 : 0) + (ownerFilter !== "all" ? 1 : 0) +
    (stepFilter !== "all" ? 1 : 0) + (calcFilter !== "all" ? 1 : 0) + (rangeFilter !== "all" ? 1 : 0);
  const resetFilters = () => {
    setSearch(""); setStatus("all"); setOwnerFilter("all");
    setStepFilter("all"); setCalcFilter("all"); setRangeFilter("all");
  };

  const pageCount = Math.max(1, Math.ceil(filteredCases.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pagedCases = useMemo(
    () => filteredCases.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [filteredCases, currentPage],
  );

  const toggleSelected = (id, e) => {
    e?.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === filteredCases.length && filteredCases.length > 0) return new Set();
      return new Set(filteredCases.map((c) => c.id));
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkDelete = async () => {
    if (!selectedIds.size) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const { data } = await api.post("/cases/bulk-delete", { case_ids: ids });
      setCases((prev) => prev.filter((c) => !selectedIds.has(c.id)));
      setSelectedIds(new Set());
      toast.success(`${data.deleted} Akte${data.deleted === 1 ? "" : "n"} gelöscht`);
    } catch (e) {
      toast.error(apiError(e, "Bulk-Löschung fehlgeschlagen"));
    } finally {
      setBulkDeleting(false);
      setConfirmBulkDelete(false);
    }
  };

  const fetch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (search) params.search = search;
      const { data } = await api.get("/cases", { params });
      setCases(data.cases);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line
  }, [status]);

  const createCase = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/cases", {});
      toast.success(`Akte ${data.case.case_number} angelegt`);
      navigate(`/cases/${data.case.id}`);
    } catch (e) {
      // Trial-Quota: Backend antwortet 402 mit code="trial_quota_exceeded"
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && typeof detail === "object" && detail.code === "trial_quota_exceeded") {
        setUpsellHeadline("Schalte unbegrenzt viele Akten frei.");
        setUpsellSub("Du hast deine kostenlose Test-Akte bereits genutzt. Wähle einen Plan und arbeite ohne Limits.");
        setUpsellOpen(true);
      } else if (e?.response?.status === 402 && typeof detail === "object" && detail.code === "onboarding_incomplete") {
        // Onboarding nicht abgeschlossen → User auf die nächste offene Aufgabe lenken
        const nextRoutes = {
          plan: "/pricing",
          profile: "/settings?tab=profile",
          sepa: "/billing/sepa",
        };
        toast.error(detail.message || "Bitte schließe das Onboarding ab.");
        const target = nextRoutes[detail.next_step];
        if (target) setTimeout(() => navigate(target), 600);
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setCreating(false);
    }
  };

  const openCase = (c) => {
    // Eigene Akten dürfen immer geöffnet werden (Trial inkl.). Verbleibende
    // Restriktionen kommen vom Backend selbst (402 wenn nötig).
    navigate(`/cases/${c.id}`);
  };

  const handleUpsell = () => {
    setUpsellHeadline("Bereit, Easy Gutachter voll zu nutzen?");
    setUpsellSub("Wähle deinen Plan und nutze sofort alle Funktionen — unbegrenzt viele Akten, DAT, KI-Texte, Backoffice.");
    setUpsellOpen(true);
  };

  const downloadPDF = async (caseId, caseNumber) => {
    setDownloadingId(caseId);
    try {
      const resp = await api.get(`/cases/${caseId}/pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Gutachten-${caseNumber || caseId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(apiError(e, "PDF-Export fehlgeschlagen"));
    } finally {
      setDownloadingId(null);
    }
  };

  const deleteCase = async () => {
    if (!confirmDelete) return;
    const c = confirmDelete;
    setDeletingId(c.id);
    try {
      await api.delete(`/cases/${c.id}`);
      setCases((prev) => prev.filter((x) => x.id !== c.id));
      toast.success(`Akte ${c.case_number} gelöscht`);
    } catch (e) {
      toast.error(apiError(e, "Akte konnte nicht gelöscht werden"));
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  };

  const counts = {
    total: cases.length,
    draft: cases.filter((c) => c.status === "draft" || c.status === "in_progress").length,
    completed: cases.filter((c) => c.status === "completed" || c.status === "closed").length,
    photos: cases.reduce((s, c) => s + (c.photos?.length || 0), 0),
  };

  return (
    <Layout
      title={`Hallo, ${user?.name?.split(" ")[0] || ""} 👋`}
      subtitle={user?.company || undefined}
      rightSlot={
        <Button onClick={createCase} disabled={creating} className="brand-gradient-bg text-white" data-testid="dashboard-new-case-btn">
          {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
          Neue Akte
        </Button>
      }
      primaryAction={{
        label: "Neue Akte",
        icon: Plus,
        onClick: createCase,
        disabled: creating,
        testid: "mobile-fab-new-case",
      }}
    >
      {/* Übernahme-Hinweis: Gutachter sieht Banner, wenn EasyGutachter eine
          seiner Akten übernommen hat (Verrechnung mit offenen Abo-Rechnungen). */}
      {user?.role === "gutachter" && <TakeoverNoticeBanner />}

      {/* Trial-Modus: Verkaufsstarker Welcome-Hero (Hero, wenn 0 Akten / kompakter Banner ab 1 Akte). */}
      {isTrial && (
        <TrialWelcomeHero
          userName={user?.name}
          casesUsed={cases.filter((c) => c.owner_id === user?.id && !c.is_sample).length}
          casesMax={1}
          onCreateCase={createCase}
          onUpsell={handleUpsell}
          creating={creating}
        />
      )}

      {/* So-funktioniert's-Block: zeigt den End-to-End-Workflow + das Win-Win-
          Modell (100 % Honorar an dich, wir verdienen nur am Abo). Wird nur
          für Trial-User angezeigt — aktive Nutzer brauchen das nicht mehr. */}
      {isTrial && (
        <HowItWorks
          onCreateCase={createCase}
          onUpsell={handleUpsell}
          creating={creating}
        />
      )}

      {/* Verdienst-Karte — für Gutachter. Desktop nur außerhalb des Trials
          (unverändert), mobil auch im Trial (nativer App-Look). */}
      {user?.role === "gutachter" && (
        isTrial
          ? <div className="lg:hidden"><EarningsBar /></div>
          : <EarningsBar />
      )}

      {/* Stats — desktop only, kompakt & eckig. */}
      {(!isTrial || cases.length > 0) && (
      <div className="hidden lg:grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {[
          { label: "Akten gesamt", value: counts.total, icon: FileText, color: "text-cyan-700 bg-cyan-50" },
          { label: "In Arbeit", value: counts.draft, icon: Clock, color: "text-amber-700 bg-amber-50" },
          { label: "Fertig", value: counts.completed, icon: TrendingUp, color: "text-[#12A9E8] bg-[#12A9E8]/10" },
          { label: "Lichtbilder", value: counts.photos, icon: Camera, color: "text-violet-700 bg-violet-50" },
        ].map((k) => (
          <div key={k.label} className="border border-zinc-200 bg-white px-4 py-3 flex items-center gap-3" data-testid={`stat-${k.label}`}>
            <div className={`h-9 w-9 ${k.color} flex items-center justify-center shrink-0`}>
              <k.icon className="h-4 w-4" strokeWidth={1.8} />
            </div>
            <div className="min-w-0">
              <div className="font-display text-2xl font-bold tracking-tight leading-none">{k.value}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500 mt-1 truncate">{k.label}</div>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Inbox: cases ready for the Gutachter to review — top of page, high-visibility.
          BLINKT, damit der Gutachter sofort sieht, dass neue Gutachten warten. */}
      {/* Der frühere Posteingang-Block wurde entfernt — gesendete Akten werden
          stattdessen direkt in der Liste hervorgehoben (oben einsortiert + "Neu"-Badge). */}

      {/* Mobile: Suche + Status + Aktualisieren im nativen App-Look */}
      <div className="lg:hidden mb-4 space-y-2.5" data-testid="dashboard-mobile-filters">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Suche nach Kennzeichen, Name, Aktennummer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12 rounded-2xl bg-white border-zinc-200 shadow-sm text-[15px]"
            data-testid="dashboard-mobile-search"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-12 rounded-2xl bg-white w-full shadow-sm border-zinc-200 text-[15px]" data-testid="dashboard-mobile-status">
            <SelectValue placeholder="Alle Status" />
          </SelectTrigger>
          <SelectContent className="rounded-xl bg-white">
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="draft">Entwurf</SelectItem>
            <SelectItem value="in_progress">In Bearbeitung</SelectItem>
            <SelectItem value="uploaded">Hochgeladen</SelectItem>
            <SelectItem value="in_review">In Prüfung</SelectItem>
            <SelectItem value="ready_for_gutachter">Zur Freigabe</SelectItem>
            <SelectItem value="completed">Fertiggestellt</SelectItem>
            <SelectItem value="closed">Abgeschlossen</SelectItem>
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={fetch}
          className="w-full h-11 rounded-2xl bg-sky-50 text-sky-600 font-semibold text-sm flex items-center justify-center gap-2 active:bg-sky-100 transition-colors"
          data-testid="dashboard-mobile-refresh"
        >
          <RefreshCw className="h-4 w-4" /> Aktualisieren
        </button>
      </div>

      {/* Filter-Leiste — alle Filter entsprechen den Tabellen-Spalten (Desktop) */}
      <div className="hidden lg:block mb-4 border border-zinc-200 bg-zinc-50 p-3" data-testid="dashboard-filterbar">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-12 gap-2 items-end">
          <div className="col-span-2 md:col-span-3 xl:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-500 mb-1">Suche</div>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                placeholder="Kennzeichen, Name, Aktennummer…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetch()}
                className="pl-9 h-9 rounded-none bg-white"
                data-testid="dashboard-search-input"
              />
            </div>
          </div>
          <div className="xl:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-500 mb-1">Status</div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 rounded-none bg-white w-full" data-testid="dashboard-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-white">
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="draft">Entwurf</SelectItem>
                <SelectItem value="in_progress">In Bearbeitung</SelectItem>
                <SelectItem value="uploaded">Hochgeladen</SelectItem>
                <SelectItem value="in_review">In Prüfung</SelectItem>
                <SelectItem value="ready_for_gutachter">Zur Freigabe</SelectItem>
                <SelectItem value="completed">Fertiggestellt</SelectItem>
                <SelectItem value="closed">Abgeschlossen</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(isAdmin || user?.role === "backoffice") && (
            <div className="xl:col-span-2">
              <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-500 mb-1">Bearbeiter</div>
              <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                <SelectTrigger className="h-9 rounded-none bg-white w-full" data-testid="dashboard-owner-filter">
                  <SelectValue placeholder="Bearbeiter" />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-white">
                  <SelectItem value="all">Alle Bearbeiter</SelectItem>
                  {owners.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="xl:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-500 mb-1">Arbeitsschritt</div>
            <Select value={stepFilter} onValueChange={setStepFilter}>
              <SelectTrigger className="h-9 rounded-none bg-white w-full" data-testid="dashboard-step-filter">
                <SelectValue placeholder="Arbeitsschritt" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-white">
                <SelectItem value="all">Alle Schritte</SelectItem>
                {WORKFLOW.map((s) => (
                  <SelectItem key={s.key} value={s.label}>{s.label}</SelectItem>
                ))}
                <SelectItem value="Fertig">Fertig</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="xl:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-500 mb-1">Kalkulation</div>
            <Select value={calcFilter} onValueChange={setCalcFilter}>
              <SelectTrigger className="h-9 rounded-none bg-white w-full" data-testid="dashboard-calc-filter">
                <SelectValue placeholder="Kalkulation" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-white">
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="not_calculated">Nicht kalkuliert</SelectItem>
                <SelectItem value="in_progress">Wird kalkuliert</SelectItem>
                <SelectItem value="calculated">Kalkuliert</SelectItem>
                <SelectItem value="completed">Abgeschlossen</SelectItem>
                <SelectItem value="sent">Versendet</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="xl:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-500 mb-1">Zeitraum</div>
            <Select value={rangeFilter} onValueChange={setRangeFilter}>
              <SelectTrigger className="h-9 rounded-none bg-white w-full" data-testid="dashboard-range-filter">
                <SelectValue placeholder="Zeitraum" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-white">
                <SelectItem value="all">Alle Zeiträume</SelectItem>
                <SelectItem value="today">Heute</SelectItem>
                <SelectItem value="7d">Letzte 7 Tage</SelectItem>
                <SelectItem value="30d">Letzte 30 Tage</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="xl:col-span-1 flex gap-2">
            <Button
              variant="outline"
              onClick={resetFilters}
              disabled={activeFilterCount === 0}
              className="h-9 rounded-none w-full px-2"
              title="Alle Filter zurücksetzen"
              data-testid="dashboard-filter-reset"
            >
              Zurücksetzen{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="text-[11px] text-zinc-500" data-testid="dashboard-filter-count">
            {filteredCases.length} von {cases.length} Akten
          </div>
          <button
            type="button"
            onClick={fetch}
            className="text-[11px] font-semibold text-zinc-600 hover:text-zinc-900 underline underline-offset-2"
            data-testid="dashboard-refresh-btn"
          >
            Aktualisieren
          </button>
        </div>
      </div>

      {/* Bulk-Aktionen: nur Admin sieht Selection-UI */}
      {isAdmin && selectedIds.size > 0 && (
        <div className="mb-4 border border-rose-200 bg-rose-50/60 px-4 py-3 flex items-center gap-3 sticky top-4 z-20 shadow-sm" data-testid="dashboard-bulk-toolbar">
          <div className="text-sm font-semibold text-rose-900">
            {selectedIds.size} Akte{selectedIds.size === 1 ? "" : "n"} ausgewählt
          </div>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={clearSelection} data-testid="dashboard-bulk-clear">
            Auswahl aufheben
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setConfirmBulkDelete(true)}
            disabled={bulkDeleting}
            data-testid="dashboard-bulk-delete"
          >
            {bulkDeleting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
            Löschen ({selectedIds.size})
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Akten…
        </div>
      ) : cases.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-14 text-center bg-zinc-50/40" data-testid="dashboard-empty-state">
          <div className="h-14 w-14 brand-gradient-soft flex items-center justify-center mx-auto mb-4">
            <FileText className="h-6 w-6 text-cyan-700" />
          </div>
          <h3 className="font-display text-xl font-bold">Noch keine Akten</h3>
          <p className="text-zinc-500 text-sm mt-2 max-w-md mx-auto">
            Legen Sie Ihre erste Akte an — in wenigen Minuten zum fertigen Gutachten.
          </p>
          <Button className="mt-5 brand-gradient-bg text-white" onClick={createCase} disabled={creating}>
            <Plus className="h-4 w-4 mr-2" /> Erste Akte anlegen
          </Button>
        </div>
      ) : filteredCases.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-10 text-center bg-zinc-50/40" data-testid="dashboard-no-results">
          <h3 className="font-display text-lg font-bold">Keine Akten entsprechen den Filtern</h3>
          <p className="text-zinc-500 text-sm mt-1">Passe die Filter an oder setze sie zurück.</p>
          <Button variant="outline" className="mt-4 rounded-none" onClick={resetFilters} data-testid="dashboard-no-results-reset">
            Filter zurücksetzen
          </Button>
        </div>
      ) : (
        <>
          {/* Mobile: WhatsApp style with swipe-to-delete — Karten im nativen App-Look */}
          <div className="lg:hidden space-y-2.5" data-testid="dashboard-mobile-list">
            {pagedCases.map((c) => (
              <MobileCaseRow
                key={c.id}
                c={c}
                isNew={isNewCase(c)}
                onRequestDelete={() => setConfirmDelete(c)}
                onOpen={() => openCase(c)}
                canEditCalcStatus={canEditCalcStatus}
                onUpdated={(updated) =>
                  setCases((prev) =>
                    prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)),
                  )
                }
              />
            ))}
          </div>

          {/* Desktop: Tabelle mit allen Spalten */}
          <div className="hidden lg:block border border-zinc-200 bg-white overflow-hidden" data-testid="dashboard-case-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-100 border-b border-zinc-200">
                  <tr className="text-left">
                    {isAdmin && (
                      <Th className="w-[40px]">
                        <input
                          type="checkbox"
                          checked={filteredCases.length > 0 && selectedIds.size === filteredCases.length}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 accent-cyan-600 cursor-pointer"
                          data-testid="dashboard-select-all"
                          aria-label="Alle auswählen"
                        />
                      </Th>
                    )}
                    <Th className="w-[80px]">Foto</Th>
                    <Th className="min-w-[200px]">Kunde / Aktenzeichen</Th>
                    <Th className="w-[60px] text-center">PDF</Th>
                    <Th className="min-w-[140px]">Arbeitsschritt</Th>
                    <Th className="min-w-[160px]">Kalkulation</Th>
                    <Th className="min-w-[120px]">Amtl. KFZ.</Th>
                    <Th className="min-w-[160px]">Hersteller</Th>
                    <Th className="min-w-[120px] text-right">Werte</Th>
                    <Th className="min-w-[140px]">Bearbeiter</Th>
                    <Th className="min-w-[140px]">Status</Th>
                    <Th className="w-[110px] text-right">Aktionen</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {pagedCases.map((c) => {
                    const meta = STATUS_META[c.status] || STATUS_META.draft;
                    const customerName = (c.customer?.firstname || c.customer?.name)
                      ? `${c.customer?.firstname || ""} ${c.customer?.name || ""}`.trim()
                      : (c.title || "Kein Kunde");
                    const initials = customerName
                      .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "–";
                    const thumb = thumbnailPath(c);
                    const make = c.vehicle?.make || "";
                    const model = c.vehicle?.model || "";
                    const hersteller = (make || model) ? `${make} ${model}`.trim() : "–";
                    const value = caseValue(c);
                    const ownerName = c.owner_name || (c.owner_id === user?.id ? user?.name : "");
                    const isOwn = c.owner_id === user?.id;
                    const ownerInitials = (ownerName || "?")
                      .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";

                    return (
                      <tr
                        key={c.id}
                        className={cn(
                          "group hover:bg-zinc-50/70 cursor-pointer transition-colors",
                          selectedIds.has(c.id) && "bg-cyan-50/60 hover:bg-cyan-50/80"
                        )}
                        onClick={() => openCase(c)}
                        data-testid={`case-table-row-${c.case_number}`}
                      >
                        {/* Checkbox (Admin only) */}
                        {isAdmin && (
                          <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(c.id)}
                              onChange={(e) => toggleSelected(c.id, e)}
                              className="h-4 w-4 accent-cyan-600 cursor-pointer"
                              data-testid={`select-case-${c.case_number}`}
                              aria-label={`Akte ${c.case_number} auswählen`}
                            />
                          </td>
                        )}
                        {/* Foto */}
                        <td className="py-2 px-3">
                          <div className="h-10 w-10 overflow-hidden bg-zinc-100 flex items-center justify-center shrink-0">
                            {thumb ? (
                              <AuthImg
                                path={thumb}
                                token={accessToken}
                                alt={customerName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full brand-gradient-bg text-white flex items-center justify-center font-display font-bold text-xs">
                                {initials === "–" ? <ImageOff className="h-4 w-4 opacity-70" /> : initials}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Kunde / Aktenzeichen */}
                        <td className="py-2 px-3">
                          <div className="font-display font-semibold text-zinc-900 truncate max-w-[280px] flex items-center gap-2">
                            <span className="truncate">{customerName}</span>
                            {c.is_sample && (
                              <span
                                className="inline-flex items-center bg-cyan-600 text-white text-[9px] font-bold px-1.5 py-0.5 uppercase tracking-wider shrink-0"
                                data-testid={`case-sample-badge-${c.case_number}`}
                              >
                                Beispiel
                              </span>
                            )}
                            {isNewCase(c) && (
                              <span
                                className="inline-flex items-center gap-1 bg-sky-600 text-white text-[9px] font-bold px-1.5 py-0.5 uppercase tracking-wider shrink-0"
                                data-testid={`case-new-badge-${c.case_number}`}
                              >
                                <span className="relative inline-flex h-1.5 w-1.5">
                                  <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping"></span>
                                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span>
                                </span>
                                Neu
                              </span>
                            )}
                          </div>
                          <div className="font-mono text-[11px] text-zinc-400 mt-0.5" data-testid={`case-reference-${c.case_number}`}>
                            {[c.case_reference, c.case_number].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(" · ") || "–"}
                          </div>
                        </td>

                        {/* Fertiges Gutachten (PDF-Symbol) — sichtbar wenn der
                            Admin einen finalen Bericht erstellt hat. Pulst grün
                            während noch "ready_for_gutachter" Status — danach
                            normal grau. Klick öffnet die letzte PDF-Version in
                            neuem Tab. Wenn keine PDF vorhanden → Platzhalter. */}
                        <td className="py-2 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                          {(c.final_reports?.length || 0) > 0 ? (
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/files/download/${encodeURI(
                                c.final_reports[c.final_reports.length - 1].storage_path
                              )}?auth=${encodeURIComponent(accessToken || "")}`}
                              target="_blank"
                              rel="noreferrer"
                              className={cn(
                                "inline-flex items-center justify-center h-8 w-8 rounded-none transition-colors",
                                c.status === "ready_for_gutachter"
                                  ? "bg-emerald-500 text-white hover:bg-emerald-600 animate-pulse"
                                  : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                              )}
                              title={
                                c.status === "ready_for_gutachter"
                                  ? "Gutachten zur Freigabe erhalten — klicken zum Öffnen"
                                  : `Finales Gutachten (v${c.final_reports.length}) öffnen`
                              }
                              data-testid={`case-final-pdf-${c.case_number}`}
                            >
                              <FileText className="h-4 w-4" />
                            </a>
                          ) : (
                            <span className="text-zinc-300" title="Noch kein finales Gutachten">
                              <FileText className="h-4 w-4 opacity-40" />
                            </span>
                          )}
                        </td>

                        {/* Arbeitsschritt */}
                        <td className="py-2 px-3">
                          <span className="inline-flex items-center gap-1.5 text-[13px] text-zinc-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-cyan-500" />
                            {nextOpenStep(c)}
                          </span>
                        </td>

                        {/* Kalkulation */}
                        <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                          <CalcStatusPicker
                            c={c}
                            canEdit={canEditCalcStatus}
                            onUpdated={(updated) =>
                              setCases((prev) =>
                                prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)),
                              )
                            }
                          />
                        </td>

                        {/* Amtl. KFZ. */}
                        <td className="py-2 px-3">
                          <span className="font-mono text-[13px] text-zinc-900">{c.vehicle?.license_plate || "–"}</span>
                        </td>

                        {/* Hersteller */}
                        <td className="py-2 px-3">
                          <span className="text-zinc-700 truncate inline-block max-w-[180px]">{hersteller}</span>
                        </td>

                        {/* Werte */}
                        <td className="py-2 px-3 text-right tabular-nums">
                          <span className={cn("font-semibold", value ? "text-emerald-700" : "text-zinc-400")}>
                            {formatEUR(value)}
                          </span>
                        </td>

                        {/* Bearbeiter */}
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <div className={cn(
                              "h-7 w-7 flex items-center justify-center font-display font-bold text-[10px] shrink-0",
                              isOwn ? "brand-gradient-bg text-white" : "bg-zinc-200 text-zinc-700"
                            )}>
                              {ownerInitials}
                            </div>
                            <span className="text-[13px] text-zinc-700 truncate max-w-[100px]">
                              {ownerName || "–"}
                            </span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="py-2 px-3">
                          <span className={cn("text-[11px] font-semibold px-2 py-1 rounded-none border inline-flex", meta.cls)}>
                            {meta.label}
                          </span>
                        </td>

                        {/* Aktionen */}
                        <td className="py-2 px-3 text-right">
                          <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              title="PDF exportieren"
                              onClick={(e) => { e.stopPropagation(); downloadPDF(c.id, c.case_number); }}
                              disabled={downloadingId === c.id}
                              data-testid={`case-action-pdf-${c.case_number}`}
                            >
                              {downloadingId === c.id
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Download className="h-4 w-4" />}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                              title="Akte löschen"
                              onClick={(e) => { e.stopPropagation(); setConfirmDelete(c); }}
                              disabled={deletingId === c.id}
                              data-testid={`case-action-delete-${c.case_number}`}
                            >
                              {deletingId === c.id
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Trash2 className="h-4 w-4" />}
                            </Button>
                            <Link
                              to={`/cases/${c.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="h-8 w-8 rounded-none hover:bg-zinc-100 inline-flex items-center justify-center"
                              title="Akte öffnen"
                              data-testid={`case-action-open-${c.case_number}`}
                            >
                              <ArrowRight className="h-4 w-4 text-zinc-500" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination — 20 Akten pro Seite */}
          {pageCount > 1 && (
            <div className="flex items-center justify-between mt-4 px-2" data-testid="dashboard-pagination">
              <div className="text-xs text-zinc-500">
                Seite <span className="font-semibold text-zinc-900">{currentPage + 1}</span> von <span className="font-semibold text-zinc-900">{pageCount}</span>
                <span className="text-zinc-400"> · {filteredCases.length} Akten</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-none border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
                  data-testid="dashboard-page-prev"
                  aria-label="Vorherige Seite"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {Array.from({ length: pageCount }).map((_, i) => {
                  // Sliding-Window für viele Seiten: aktuelle ± 1, plus erste/letzte
                  if (pageCount > 7) {
                    const showFirst = i === 0;
                    const showLast = i === pageCount - 1;
                    const inWindow = Math.abs(i - currentPage) <= 1;
                    if (!showFirst && !showLast && !inWindow) {
                      if (i === 1 && currentPage > 3) return <span key={`e-${i}`} className="px-1 text-zinc-300">…</span>;
                      if (i === pageCount - 2 && currentPage < pageCount - 4) return <span key={`e-${i}`} className="px-1 text-zinc-300">…</span>;
                      return null;
                    }
                  }
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPage(i)}
                      className={cn(
                        "inline-flex items-center justify-center h-8 min-w-[32px] px-2 rounded-none border text-xs font-semibold transition",
                        i === currentPage
                          ? "border-cyan-600 bg-cyan-600 text-white"
                          : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50",
                      )}
                      data-testid={`dashboard-page-${i + 1}`}
                    >
                      {i + 1}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setPage(Math.min(pageCount - 1, currentPage + 1))}
                  disabled={currentPage === pageCount - 1}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-none border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
                  data-testid="dashboard-page-next"
                  aria-label="Nächste Seite"
                >
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Churn-Insights: nur Admin — bewusst ganz unten, damit die
          Akten-Liste (das eigentliche Arbeitswerkzeug) oben bleibt. */}
      {isAdmin && !loading && <ChurnInsightsWidget />}

      {/* Confirm Delete dialog — used by both desktop trash button & mobile swipe */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="case-delete-confirm">
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <AlertDialogTitle>Akte wirklich löschen?</AlertDialogTitle>
                <AlertDialogDescription>
                  {confirmDelete && (
                    <>Akte <strong>{confirmDelete.case_number}</strong>
                      {confirmDelete.customer?.name ? ` (${confirmDelete.customer.name})` : ""} wird unwiderruflich gelöscht. Alle Lichtbilder, Dokumente und der Verlauf gehen verloren.</>
                  )}
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="case-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteCase}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="case-delete-confirm-btn"
            >
              {deletingId ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk-Delete-Confirm */}
      <AlertDialog open={confirmBulkDelete} onOpenChange={(v) => !v && setConfirmBulkDelete(false)}>
        <AlertDialogContent data-testid="bulk-delete-confirm">
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <AlertDialogTitle>{selectedIds.size} Akte{selectedIds.size === 1 ? "" : "n"} löschen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Diese Aktion ist <strong>nicht rückgängig zu machen</strong>. Alle Lichtbilder,
                  Dokumente und der Verlauf der ausgewählten Akten werden unwiderruflich gelöscht.
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="bulk-delete-confirm-btn"
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              {selectedIds.size} Akte{selectedIds.size === 1 ? "" : "n"} endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Verkaufsstarker Upsell-Dialog — wird bei Trial-Quota-Überschreitung
          oder Klick auf „Plan wählen" angezeigt. */}
      <UpsellDialog
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        headline={upsellHeadline}
        subheadline={upsellSub}
        trigger="dashboard"
      />
    </Layout>
  );
}

function Th({ children, className }) {
  return (
    <th
      className={cn(
        "py-2 px-3 text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500",
        className
      )}
    >
      {children}
    </th>
  );
}

function MobileCaseRow({ c, isNew = false, onRequestDelete, onOpen, canEditCalcStatus, onUpdated }) {
  const { accessToken } = useAuth();
  const meta = STATUS_META[c.status] || STATUS_META.draft;
  const displayName = c.customer?.name || "";
  const title = displayName || `Akte ${c.case_number || ""}`.trim();
  const vehicle = c.vehicle?.make ? `${c.vehicle.make} ${c.vehicle.model || ""}`.trim() : "Kein Fahrzeug";
  const plate = c.vehicle?.license_plate || "";
  const subtitle = plate ? `${vehicle} · ${plate}` : vehicle;
  const thumbPath = c.photos?.[0]?.storage_path || null;
  const updated = c.updated_at ? new Date(c.updated_at) : null;
  const timeLabel = updated
    ? (new Date().toDateString() === updated.toDateString()
        ? updated.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
        : updated.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }))
    : "";

  // ---- Swipe-to-delete (touch-only, simple translate-X tracking) ----
  const navigate = useNavigate();
  const startX = React.useRef(0);
  const startY = React.useRef(0);
  const isDragging = React.useRef(false);
  const blockClick = React.useRef(false);
  const [offset, setOffset] = useState(0);
  const SWIPE_THRESHOLD = 80;   // px from where the delete-button stays open
  const TRIGGER_DISTANCE = 120; // px → instant confirm dialog

  const onTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    isDragging.current = true;
  };
  const onTouchMove = (e) => {
    if (!isDragging.current) return;
    const dx = e.touches[0].clientX - startX.current;
    const dy = Math.abs(e.touches[0].clientY - startY.current);
    // Only react to predominantly horizontal swipes (vertical scroll wins)
    if (dy > Math.abs(dx) && Math.abs(dx) < 14) return;
    if (dx < 0) setOffset(Math.max(dx, -160));
    else setOffset(Math.min(dx * 0.2, 12)); // tiny rubber-band on right
  };
  const onTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    if (offset <= -TRIGGER_DISTANCE) {
      // hard swipe → open confirm dialog directly
      blockClick.current = true;
      setOffset(0);
      onRequestDelete?.();
      setTimeout(() => { blockClick.current = false; }, 50);
    } else if (offset <= -SWIPE_THRESHOLD) {
      // soft swipe → reveal delete button only
      setOffset(-88);
      blockClick.current = true;
      setTimeout(() => { blockClick.current = false; }, 50);
    } else {
      setOffset(0);
    }
  };

  const handleClick = (e) => {
    // Don't navigate while a swipe-state is open / right after a swipe.
    if (offset !== 0 || blockClick.current) {
      e.preventDefault();
      // tapping outside delete area while open → close it
      setOffset(0);
      return;
    }
    onOpen?.();
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm" data-testid={`case-row-${c.case_number}`}>
      {/* Red delete strip behind the row (revealed on swipe) */}
      <button
        onClick={(e) => { e.stopPropagation(); setOffset(0); onRequestDelete?.(); }}
        className={cn(
          "absolute inset-y-0 right-0 bg-rose-600 text-white px-5 flex items-center justify-center gap-1.5 active:bg-rose-700 transition-opacity",
          offset <= -SWIPE_THRESHOLD ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        style={{ width: "88px" }}
        aria-label="Akte löschen"
        data-testid={`case-row-swipe-delete-${c.case_number}`}
      >
        <Trash2 className="h-5 w-5" />
        <span className="text-[11px] font-semibold">Löschen</span>
      </button>

      {/* Foreground row */}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={handleClick}
        className={cn(
          "flex items-center gap-3 py-3.5 px-3.5 transition-colors cursor-pointer",
          "bg-white active:bg-zinc-50",
          isDragging.current ? "transition-none" : "transition-transform duration-200 ease-out"
        )}
        style={{ transform: `translateX(${offset}px)` }}
      >
        {/* Foto-Thumbnail (erstes Aktenfoto) oder Platzhalter — nativer App-Look */}
        <div className="h-12 w-12 rounded-xl bg-zinc-100 overflow-hidden flex items-center justify-center shrink-0">
          {thumbPath ? (
            <AuthImg path={thumbPath} token={accessToken} alt="" className="w-full h-full object-cover" />
          ) : (
            <CarFront className="h-5 w-5 text-zinc-300" strokeWidth={1.8} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-display font-bold text-[15px] truncate text-zinc-900 flex items-center gap-1.5">
              <span className="truncate">{title}</span>
              {c.is_sample && (
                <span className="bg-cyan-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider shrink-0" data-testid={`case-card-sample-badge-${c.case_number}`}>Beispiel</span>
              )}
              {isNew && (
                <span className="bg-sky-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider shrink-0">Neu</span>
              )}
            </div>
            <span className="text-[11px] text-zinc-400 shrink-0 tabular-nums">{timeLabel}</span>
          </div>
          {(c.case_reference || c.case_number) && (
            <div className="font-mono text-[11px] text-zinc-400 mt-0.5 truncate" data-testid={`case-row-reference-${c.case_number}`}>
              {c.case_reference || c.case_number}
            </div>
          )}
          <div className="text-[13px] text-zinc-500 truncate mt-0.5">{subtitle}</div>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <CalcStatusPicker c={c} canEdit={canEditCalcStatus} onUpdated={onUpdated} compact />
            <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full border", meta.cls)}>
              {meta.label}
            </span>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-zinc-300 shrink-0" strokeWidth={2} />
      </div>
    </div>
  );
}
